import {
  BackLink,
  DeleteConfirmation,
  FiltersBar,
  IconButton,
  ListView,
  ListViewActions,
  ListViewMenu,
  Page,
  SearchInput,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import MemberTemplateDrawer from './MemberTemplateDrawer';

const StyledDescription = styled.p`
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 30rem;
  white-space: nowrap;
`;

const memberTypeNames = {
  employee: 'Employee',
  contractor: 'Contractor',
  other: 'Other',
};

function MemberTemplatesPage() {
  const documentTitle = useDocumentTitle('Member Templates');

  const api = useApi();
  const { workspace } = useWorkspace();
  const confirmation = useConfirmation();
  const toast = useToast();
  const [query, setQuery] = useState({ isReady: false, data: null, error: null });
  const [params, setParams] = useState({ q: '', size: 1000 });
  const [dialog, setDialog] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).memberTemplates().get(params);
      setQuery({ isReady: true, data, error: null });
    } catch (error) {
      setQuery({ isReady: true, data: null, error });
    }
  }, [api, workspace.id, params]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = ({ target: { name, value } }) => {
    setParams((state) => ({ ...state, [name]: value }));
  };

  const handleEdit = (template) => {
    setDialog(
      <MemberTemplateDrawer
        template={template}
        onClose={() => {
          setDialog(null);
          documentTitle.set('Member Templates');
        }}
        onDeleted={fetchData}
        onSaved={fetchData}
      />,
    );
  };

  const handleDelete = async (template) => {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>Are you sure you want to delete this Member Template?</DeleteConfirmation>
    ));
    if (!confirm) return;

    try {
      await api.www.workspaces(workspace.id).memberTemplates(template.id).delete();
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  };

  if (!query.isReady) return <PageLoader />;
  if (!query.data) return <ErrorPage publicSite={false} />;

  return (
    <>
      <Page.Header>
        <BackLink defaultPath={`/app/${workspace.key}/settings/custom-data`} />

        <Page.Info>
          <Page.Title>Member Templates</Page.Title>
          <Page.Description>
            Member templates enable you to more consistently create new workspace members.
          </Page.Description>
        </Page.Info>

        <Page.Actions>
          <IconButton icon="plus" tooltip="New Member Template" onClick={() => handleEdit({})} />
        </Page.Actions>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <SearchInput name="q" value={params.q} placeholder="Search" onChange={handleFilterChange} />
        </FiltersBar>
      </Page.Filters>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column width="18rem">Name</ListView.Column>
            <ListView.Column>Description</ListView.Column>
            <ListView.Column width="12rem">Member Type</ListView.Column>
            <ListView.Column width="14rem">Security Role</ListView.Column>
            <ListViewActions.Column />
          </ListView.Header>

          <ListView.Body>
            {query.data.map((template) => (
              <ListView.Row key={template.id} onClick={() => handleEdit(template)}>
                <ListView.Cell>{template.name}</ListView.Cell>
                <ListView.Cell>
                  <StyledDescription>{template.description}</StyledDescription>
                </ListView.Cell>
                <ListView.Cell>{memberTypeNames[template.employmentTypeId] || '—'}</ListView.Cell>
                <ListView.Cell>{template.securityRole?.name || '—'}</ListView.Cell>
                <ListViewActions>
                  <ListViewActions.Edit onClick={() => handleEdit(template)} />

                  <hr />

                  <ListViewMenu>
                    {({ setIsOpen }) => (
                      <>
                        <ListViewMenu.Item
                          onClick={() => {
                            handleEdit(template);
                            setIsOpen(false);
                          }}>
                          Edit
                        </ListViewMenu.Item>

                        <ListViewMenu.DeleteItem
                          onClick={() => {
                            handleDelete(template);
                            setIsOpen(false);
                          }}>
                          Delete
                        </ListViewMenu.DeleteItem>
                      </>
                    )}
                  </ListViewMenu>
                </ListViewActions>
              </ListView.Row>
            ))}

            {query.data.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={query.data.length} label="Member Template" />
        </ListView>
      </Page.ListView>

      {dialog}
    </>
  );
}

export default MemberTemplatesPage;
